import React, {
  ReactElement, useCallback, useContext, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import striptags from 'striptags';
import geodist from 'geodist';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMars, faVenus } from '@fortawesome/free-solid-svg-icons';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import aa from 'search-insights';
import { CAMERA_ICON_IMAGE, LISTING_BOOSTED_BADGE_IMAGE, PUPPY_DEFAULT_IMAGE } from '../../../utils/constants';
import Favourite from '../../favourite';
import DateHelper from '../../../utils/DateHelper';
import CompassSVG from '../../svgComponents/CompassSVG';
import { LocationParams } from '../../search/admin-dashboard-tables/interfaces';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import InformationCircleSVG from '../../svgComponents/InformationCircleSVG';
import { cloudinaryTransform } from '../../../utils/cloudinaryTransformations';
import { setFallbackImage } from '../../../utils/imageHelper';
import {
  AdType, DogSex, ListingAvailability, ListingType,
} from '../../../structures/models/listings';
import { SellerTier } from '../../../structures/models/seller';
import { Generation } from '../../../structures/models/litter';
import { AlgoliaContext } from '../block/search';

function formatPrice(price) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(price / 100);
}

interface Props {
  puppy: PuppyCardInterface,
  renderSimplePreview?: boolean,
}

export interface PuppyCardInterface {
  id: number,
  name: string,
  ad_type: AdType,
  verified: SellerTier,
  _geoloc: GeoLocation,
  availability: ListingAvailability,
  route: string,
  thumbnailUrl: string,
  has_video: boolean,
  sex: DogSex,
  birthDate: string,
  admin_breed_display: string,
  description: string,
  price: number,
  sale_price: number,
  real_price: number,
  city: string,
  state: string,
  registrations: string[],
  generation: Generation | null,
  date_available: number,
  size: string,
  listingType: ListingType,
  is_boosted: boolean,
  isInitiallyFavorited?: boolean;
  photo_gallery: Array<{ file_url: string }> | undefined,
}

interface GeoLocation {
  lat: number,
  lng: number,
}

const PuppyCard: React.FC<Props> = ({ puppy, renderSimplePreview }): ReactElement => {
  const indexName = useContext(AlgoliaContext);
  const [distance, setDistance] = useState<number | null>(null);
  const [lat, setLat] = useState<string | null>('');
  const [lon, setLon] = useState<string | null>('');

  const isPremium = puppy.ad_type === 'premium';
  const isBoosted = puppy.is_boosted;
  const showVerified = isPremium && puppy.verified === 'verified';
  const classNames = isPremium || isBoosted ? 'puppy-card premium' : 'puppy-card';
  const puppyImageAlt = `${puppy.admin_breed_display} ${puppy.listingType === 'puppy' ? 'puppies' : 'studs'} for sale in ${puppy.city}, ${puppy.state} - ${puppy.name}`;

  const updateCoordinates = useCallback(() => {
    const initialLat = localStorage.getItem('latitude');
    const initialLon = localStorage.getItem('longitude');
    if (lat !== initialLat) {
      setLat(initialLat);
    }
    if (lon !== initialLon) {
      setLon(initialLon);
    }
  }, [lat, lon]);

  const updateLocation = useCallback((e: any) => {
    const { detail } = e as { detail: { newLocation: boolean } };
    if (detail && detail.newLocation) {
      updateCoordinates();
    }
  }, [updateCoordinates]);

  useEffect(() => {
    const {
      lat: latQuery,
      lng: lonQuery,
    }: LocationParams = fetchSearchParams();
    if (latQuery && lonQuery) {
      setLat(latQuery);
      setLon(lonQuery);
    } else {
      updateCoordinates();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('updateLocation', updateLocation);
    return () => document.removeEventListener('updateLocation', updateLocation);
  }, [updateLocation]);

  useEffect(() => {
    if (lat && lon) {
      let tempDist = null;
      // eslint-disable-next-line no-underscore-dangle
      const puppyLoc = puppy._geoloc;
      if (puppyLoc.lat && puppyLoc.lng) {
        tempDist = geodist(
          {
            lat: puppyLoc.lat,
            lon: puppyLoc.lng,
          },
          {
            lat,
            lon,
          },
          {
            unit: 'miles',
          },
        );
        if (tempDist !== distance) {
          setDistance(tempDist);
        }
      }
    }
  }, [distance, puppy, lat, lon]);

  const mods = [
    't_lpr-full-transform',
    'c_thumb,h_232,w_278',
  ];

  if (puppy.availability === 'pending') {
    mods.push('$text_!PENDING SALE!');
    mods.push('t_lpr-add-text-transform');
  }

  if (renderSimplePreview) {
    return (
      <div className={classNames}>
        <div className="position-relative card-img-top">
          <Favourite hit={puppy} isInitiallyFavorited={puppy.isInitiallyFavorited} />
          {puppy.photo_gallery && puppy.photo_gallery.length > 1 && isBoosted ? (
            <div className="puppy-gallery-grid">
              <div className="main-image">
                <LazyLoadImage
                  onError={(e) => {
                    setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                  }}
                  src={puppy.photo_gallery[0].file_url
                    ? cloudinaryTransform(
                      puppy.photo_gallery[0].file_url,
                      mods,
                    ) : PUPPY_DEFAULT_IMAGE}
                  alt={puppyImageAlt}
                />
                {puppy.photo_gallery !== undefined && puppy.photo_gallery.length > 1 && (
                  <div
                    className="puppy-gallery-info"
                  >
                    <img alt="Number of images icon" src={CAMERA_ICON_IMAGE} />
                    <span>{puppy.photo_gallery?.length}</span>
                  </div>
                )}
              </div>
              <div className="secondary-images">
                <LazyLoadImage
                  onError={(e) => {
                    setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                  }}
                  src={puppy.photo_gallery[1]?.file_url
                    ? cloudinaryTransform(
                      puppy.photo_gallery[1].file_url,
                      mods,
                    ) : PUPPY_DEFAULT_IMAGE}
                  alt={puppyImageAlt}
                />
                <LazyLoadImage
                  onError={(e) => {
                    setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                  }}
                  src={puppy.photo_gallery[2]?.file_url
                    ? cloudinaryTransform(
                      puppy.photo_gallery[2].file_url,
                      mods,
                    ) : PUPPY_DEFAULT_IMAGE}
                  alt={puppyImageAlt}
                />
              </div>
            </div>
          ) : (
            <LazyLoadImage
              className="single-photo"
              onError={(e) => {
                setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
              }}
              src={puppy.thumbnailUrl
                ? cloudinaryTransform(
                  puppy.thumbnailUrl,
                  mods,
                ) : PUPPY_DEFAULT_IMAGE}
              alt={puppyImageAlt}
            />
          )}
        </div>
        <div className="puppy-info-container">
          <div className="top-container d-flex justify-content-between">
            <div className="top-left-container d-flex justify-content-left">
              {puppy.ad_type !== 'basic' && (
                <p style={{ marginBottom: '0px' }}><strong>{puppy.name}</strong></p>
              )}
              {puppy.ad_type === 'basic' && (
                <p style={{ marginBottom: '0px' }}>{puppy.name}</p>
              )}
              {typeof puppy?.sex === 'string' && puppy.sex.toLowerCase() === 'male' && (
                <FontAwesomeIcon icon={faMars} className="male-icon my-1 mx-2" />
              )}
              {typeof puppy?.sex === 'string' && puppy.sex.toLowerCase() === 'female' && (
                <FontAwesomeIcon icon={faVenus} className="female-icon my-1 mx-2" />
              )}
            </div>
            <div className="d-flex flex-column badge-container">
              {isBoosted && (
                <img
                  src={LISTING_BOOSTED_BADGE_IMAGE}
                  className="my-1 ml-auto boost-badge"
                  alt="Boosted"
                  style={{ height: '20px' }}
                />
              )}
              {showVerified && (
                <span className="badge badge-lg badge-yellow my-1 badge-pill py-1">
                  <i className="fas fa-award fa-xs mr-2" />
                  <strong>
                    Accredited Seller
                  </strong>
                </span>
              )}
            </div>
          </div>
          <div>
            <span className="badge badge-lg badge-l-gray my-auto badge-pill float-right">
              {DateHelper.getHumanReadableAge(puppy.birthDate)}
            </span>
            <p className="puppy-breed my-0">{puppy.admin_breed_display}</p>
          </div>
          {puppy.ad_type !== 'basic' && (
            <div
              className="puppy-description my-0 text-muted"
            >
              {striptags(puppy.description)}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <span className="puppy-price">
              {formatPrice(puppy.real_price)}
            </span>
            {typeof distance === 'number' && (
              <span className="d-flex align-items-center">
                <CompassSVG />
                <span className="ml-1 distance-text">
                  {`${distance} mi`}
                </span>
              </span>
            )}
            <div className="d-flex align-items-center">
              {puppy.city != null && (
                <span className="badge badge-lg badge-l-gray my-auto badge-pill mr-2">
                  {`${puppy.city}, ${puppy.state}`}
                </span>
              )}
              <div
                className="d-flex justify-content-center align-items-center border-0 bg-transparent js-tooltip-button"
              >
                <InformationCircleSVG />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div key={puppy.id} className={classNames}>
        <a
          href={puppy.route}
          onClick={(e) => {
            e.preventDefault();
            const { nodeName } = e.target as Element;
            if (nodeName) {
              const nodeClicked = nodeName.toLowerCase() as String;
              if (nodeClicked === 'svg' || nodeClicked === 'button' || nodeClicked === 'i') {
                return;
              }
            }

            // @ts-ignore
            // eslint-disable-next-line no-underscore-dangle
            if (indexName && puppy.objectID && puppy.__queryID && puppy.__position) {
              const url = new URL(puppy.route, window.location.origin);
              // @ts-ignore
              // eslint-disable-next-line no-underscore-dangle
              const queryId = puppy.__queryID;
              // @ts-ignore
              const objectId = puppy.objectID;
              // @ts-ignore
              // eslint-disable-next-line no-underscore-dangle
              const position = puppy.__position;
              url.searchParams.append('algoliaIndexName', indexName);
              url.searchParams.append('algoliaQueryId', queryId);
              url.searchParams.append('algoliaObjectId', objectId);

              aa('clickedObjectIDsAfterSearch', {
                eventName: 'Listing visited',
                index: indexName,
                queryID: queryId,
                objectIDs: [objectId],
                positions: [position],
              }).then(() => {
                window.location.href = url.toString();
              }).catch(() => {
                window.location.href = url.toString();
              });

              return;
            }

            window.location.href = puppy.route;
          }}
        >
          <div className="position-relative card-img-top">
            <Favourite hit={puppy} isInitiallyFavorited={puppy.isInitiallyFavorited} />
            {puppy.photo_gallery && puppy.photo_gallery.length > 1 && isBoosted ? (
              <div className="puppy-gallery-grid">
                <div className="main-image">
                  <LazyLoadImage
                    onError={(e) => {
                      setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                    }}
                    src={puppy.photo_gallery[0].file_url
                      ? cloudinaryTransform(
                        puppy.photo_gallery[0].file_url,
                        mods,
                      ) : PUPPY_DEFAULT_IMAGE}
                    alt={puppyImageAlt}
                  />
                  {puppy.photo_gallery !== undefined && puppy.photo_gallery.length > 1 && (
                    <div
                      className="puppy-gallery-info"
                    >
                      <img alt="Number of images icon" src={CAMERA_ICON_IMAGE} />
                      <span>{puppy.photo_gallery?.length}</span>
                    </div>
                  )}
                </div>
                <div className="secondary-images">
                  <LazyLoadImage
                    onError={(e) => {
                      setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                    }}
                    src={puppy.photo_gallery[1]?.file_url
                      ? cloudinaryTransform(
                        puppy.photo_gallery[1].file_url,
                        mods,
                      ) : PUPPY_DEFAULT_IMAGE}
                    alt={puppyImageAlt}
                  />
                  <LazyLoadImage
                    onError={(e) => {
                      setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                    }}
                    src={puppy.photo_gallery[2]?.file_url
                      ? cloudinaryTransform(
                        puppy.photo_gallery[2].file_url,
                        mods,
                      ) : PUPPY_DEFAULT_IMAGE}
                    alt={puppyImageAlt}
                  />
                </div>
              </div>
            ) : (
              <LazyLoadImage
                className="single-photo"
                onError={(e) => {
                  setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                }}
                src={puppy.thumbnailUrl
                  ? cloudinaryTransform(
                    puppy.thumbnailUrl,
                    mods,
                  ) : PUPPY_DEFAULT_IMAGE}
                alt={puppyImageAlt}
              />
            )}
          </div>
          <div className="puppy-info-container">
            <div className="top-container d-flex justify-content-between">
              <div className="top-left-container d-flex justify-content-left">
                {puppy.ad_type !== 'basic' && (
                  <p style={{ marginBottom: '0px' }}><strong>{puppy.name}</strong></p>
                )}
                {puppy.ad_type === 'basic' && (
                  <p style={{ marginBottom: '0px' }}>{puppy.name}</p>
                )}
                {typeof puppy?.sex === 'string' && puppy.sex.toLowerCase() === 'male' && (
                  <FontAwesomeIcon icon={faMars} className="male-icon my-1 mx-2" />
                )}
                {typeof puppy?.sex === 'string' && puppy.sex.toLowerCase() === 'female' && (
                  <FontAwesomeIcon icon={faVenus} className="female-icon my-1 mx-2" />
                )}
              </div>
              <div className="d-flex flex-column badge-container">
                {isBoosted && (
                  <img
                    src={LISTING_BOOSTED_BADGE_IMAGE}
                    className="my-1 ml-auto boost-badge"
                    alt="Boosted"
                    style={{ height: '20px' }}
                  />
                )}
                {showVerified && (
                  <span className="badge badge-lg badge-yellow my-1 badge-pill py-1">
                    <i className="fas fa-award fa-xs mr-2" />
                    <strong>
                      Accredited Seller
                    </strong>
                  </span>
                )}
              </div>
            </div>
            <div>
              <span className="badge badge-lg badge-l-gray my-auto badge-pill float-right">
                {DateHelper.getHumanReadableAge(puppy.birthDate)}
              </span>
              <p className="puppy-breed my-0">{puppy.admin_breed_display}</p>
            </div>
            {puppy.ad_type !== 'basic' && (
              <div
                className="puppy-description my-0 text-muted"
              >
                {striptags(puppy.description)}
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <span className="puppy-price">
                {formatPrice(puppy.real_price)}
              </span>
              {typeof distance === 'number' && (
                <span className="d-flex align-items-center">
                  <CompassSVG />
                  <span className="ml-1 distance-text">
                    {`${distance} mi`}
                  </span>
                </span>
              )}
              <div className="d-flex align-items-center">
                {puppy.city != null && (
                  <span className="badge badge-lg badge-l-gray my-auto badge-pill mr-2">
                    {`${puppy.city}, ${puppy.state}`}
                  </span>
                )}
                <OverlayTrigger
                  placement="top-end"
                  delay={{ show: 100, hide: 500 }}
                  trigger={['hover', 'focus', 'click']}
                  overlay={
                    (props) => (
                      <Tooltip
                        id={`${puppy.name}-${puppy.id}-tooltip`}
                        bsPrefix="hover-tool-tip-puppy-info"
                        {...props}
                      >
                        <div className="d-flex flex-column">
                          {
                            puppy.registrations.length > 0 && (
                              <div className="d-flex justify-content-between border-bottom border-border-gray py-2">
                                <p className="text-black font-weight-bold mb-0 mr-2">Registration:</p>
                                <p className="text-blue-400 text-capitalize mb-0">{puppy.registrations.join(', ')}</p>
                              </div>
                            )
                          }
                          {!!puppy.generation && (
                            <div className="d-flex justify-content-between border-bottom border-border-gray py-2">
                              <p className="text-black font-weight-bold mb-0 mr-2">Generation:</p>
                              <p className="text-blue-400 mb-0 ">
                                {puppy.generation}
                              </p>
                            </div>
                          )}
                          <div className="d-flex justify-content-between border-bottom border-border-gray py-2">
                            <p className="text-black font-weight-bold mb-0 mr-2">Available on:</p>
                            <p className="text-blue-400 mb-0 ">
                              {DateHelper.getUtcDateTimeFromTimestamp(puppy.date_available).format('LL')}
                            </p>
                          </div>
                          {
                            !!puppy.size && (
                              <div className="d-flex justify-content-between py-2">
                                <p className="text-black font-weight-bold mb-0 mr-2">Size:</p>
                                <p className="text-blue-400 text-capitalize mb-0">{puppy.size}</p>
                              </div>
                            )
                          }
                        </div>
                      </Tooltip>
                    )
                  }
                >
                  {({ ref, ...triggerHandler }) => (
                    <button
                      className="d-flex justify-content-center align-items-center border-0 bg-transparent js-tooltip-button"
                      ref={ref}
                      {...triggerHandler}
                    >
                      <InformationCircleSVG />
                    </button>
                  )}
                </OverlayTrigger>
              </div>
            </div>
          </div>
        </a>
      </div>
    </>
  );
};

if (document.getElementsByClassName('react-puppy-card').length > 0) {
  document.querySelectorAll('.react-puppy-card')
    .forEach((element) => {
      const puppyData = element.getAttribute('data-puppy');

      // If the data-puppy attribute was not supplied, we cannot initialize react. Skip
      if (puppyData) {
        const puppy = JSON.parse(puppyData);
        ReactDOM.render(<PuppyCard puppy={puppy} />, element);
      }
    });
}

export default PuppyCard;
